import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Avatar,
} from '@mui/material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaFilter from 'components/SparaFilter/SparaFilter';

import { fetcher, loginActivityApi } from '../../lib/api';
import styles from './activityLogins.module.scss';

const PAGE_SIZE = 20;
const REFRESH_MS = 1000;
const SKELETON_COUNT = 10;

const mergeLogins = (prev, incoming) => {
    const byId = new Map(prev.map((row) => [row.id, row]));
    for (const row of incoming) {
        byId.set(row.id, row);
    }
    return Array.from(byId.values()).sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
};

const methodBadgeStyles = {
    sim: {
        background: 'var(--spara-badge-info-bg)',
        color: 'var(--spara-badge-info-fg)',
    },
    app: {
        background: 'var(--spara-badge-purple-bg)',
        color: 'var(--spara-badge-purple-fg)',
    },
    sms: {
        background: 'var(--spara-badge-green-bg)',
        color: 'var(--spara-badge-green-fg)',
    },
    password: {
        background: 'var(--spara-badge-orange-bg)',
        color: 'var(--spara-badge-orange-fg)',
    },
    passkey: {
        background: 'var(--spara-badge-green-bg)',
        color: 'var(--spara-badge-green-fg)',
    },
    password_passkey: {
        background: 'var(--spara-badge-neutral-bg)',
        color: 'var(--spara-badge-neutral-fg)',
    },
    link: {
        background: 'var(--spara-badge-info-bg)',
        color: 'var(--spara-badge-info-fg)',
    },
};

const typeBadgeStyles = {
    admin: {
        background: 'var(--spara-badge-red-bg)',
        color: 'var(--spara-badge-red-fg)',
    },
    app: {
        background: 'var(--spara-badge-indigo-bg)',
        color: 'var(--spara-badge-indigo-fg)',
    },
};

const deviceBadgeStyles = {
    desktop: {
        background: 'var(--spara-badge-neutral-bg)',
        color: 'var(--spara-badge-neutral-fg)',
    },
    mobile: {
        background: 'var(--spara-badge-cyan-bg)',
        color: 'var(--spara-badge-cyan-fg)',
    },
    tablet: {
        background: 'var(--spara-badge-purple-bg)',
        color: 'var(--spara-badge-purple-fg)',
    },
    app: {
        background: 'var(--spara-badge-green-bg)',
        color: 'var(--spara-badge-green-fg)',
    },
};

const defaultBadgeStyle = {
    background: 'var(--spara-badge-neutral-bg)',
    color: 'var(--spara-badge-neutral-fg)',
};

const generateColor = (id) => {
    if (!id) {
        return {
            bg: 'var(--spara-badge-neutral-bg)',
            text: 'var(--spara-badge-neutral-fg)',
        };
    }
    const hash = String(id)
        .split('')
        .reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) % 360, 0);
    return {
        bg: `hsl(${hash}, 55%, var(--spara-avatar-bg-l, 88%))`,
        text: `hsl(${hash}, 55%, var(--spara-avatar-fg-l, 30%))`,
    };
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const sec = String(d.getSeconds()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}:${sec}`;
};

const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = Math.max(0, now - d);
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years} ár síðan`;
    if (months > 0) return `${months} ${months === 1 ? 'mánuður' : 'mánuðir'} síðan`;
    if (days > 0) return `${days} ${days === 1 ? 'dagur' : 'dagar'} síðan`;
    if (hours > 0) return `${hours} ${hours === 1 ? 'klukkustund' : 'klukkustundir'} síðan`;
    if (minutes > 0) return `${minutes} ${minutes === 1 ? 'mínúta' : 'mínútur'} síðan`;
    return `${seconds} ${seconds === 1 ? 'sekúnda' : 'sekúndur'} síðan`;
};

const LoginRowSkeleton = () => (
    <TableRow>
        <TableCell>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Skeleton variant="circular" width={38} height={38} />
                <div>
                    <Skeleton variant="text" width={120} height={17} />
                    <Skeleton variant="text" width={90} height={14} sx={{ mt: 0.3 }} />
                </div>
            </div>
        </TableCell>
        <TableCell>
            <Skeleton variant="rounded" width={52} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="rounded" width={52} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="rounded" width={70} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={140} />
        </TableCell>
        <TableCell>
            <Skeleton variant="rounded" width={40} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="rounded" width={56} height={20} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={110} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={90} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={80} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={120} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={90} />
        </TableCell>
        <TableCell>
            <Skeleton variant="text" width={110} />
        </TableCell>
    </TableRow>
);

const ActivityLogins = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        login_method: null,
        login_type: null,
        status: null,
    });
    const [allLogins, setAllLogins] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loadedPages, setLoadedPages] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const apiFilters = useMemo(
        () => ({
            ...(filters.login_method ? { login_method: filters.login_method } : {}),
            ...(filters.login_type ? { login_type: filters.login_type } : {}),
            ...(filters.status ? { status: filters.status } : {}),
        }),
        [filters]
    );

    const listKey = loginActivityApi.list(1, PAGE_SIZE, apiFilters);

    const { data, isLoading } = useSWR(listKey, fetcher, {
        refreshInterval: REFRESH_MS,
        revalidateOnFocus: true,
    });

    useEffect(() => {
        setAllLogins([]);
        setLoadedPages(1);
        setTotalCount(0);
    }, [listKey]);

    useEffect(() => {
        if (!data?.results) return;
        setAllLogins((prev) => mergeLogins(prev, data.results));
        setTotalCount(data.count || 0);
    }, [data]);

    const showingSkeleton = isLoading && allLogins.length === 0;
    const hasMore = allLogins.length < totalCount;

    const handleSearch = useCallback((e) => {
        const value = e?.target?.value ?? e ?? '';
        setSearchQuery(value);
    }, []);

    const handleFilterClick = useCallback((filterType, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterType]: prev[filterType] === value ? null : value,
        }));
    }, []);

    const handleClearAll = useCallback(() => {
        setFilters({
            login_method: null,
            login_type: null,
            status: null,
        });
        setSearchQuery('');
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (isLoadingMore || !hasMore) return;
        setIsLoadingMore(true);
        try {
            const nextPage = loadedPages + 1;
            const res = await fetcher(loginActivityApi.list(nextPage, PAGE_SIZE, apiFilters));
            if (res?.results?.length) {
                setAllLogins((prev) => mergeLogins(prev, res.results));
                setLoadedPages(nextPage);
            }
            if (typeof res?.count === 'number') {
                setTotalCount(res.count);
            }
        } finally {
            setIsLoadingMore(false);
        }
    }, [apiFilters, hasMore, isLoadingMore, loadedPages]);

    const filterGroups = useMemo(
        () => [
            [
                {
                    id: 'login_method',
                    label: 'login_method_sim',
                    value: 'sim',
                    color: '#1565c0',
                },
                {
                    id: 'login_method',
                    label: 'login_method_app',
                    value: 'app',
                    color: '#6a1b9a',
                },
                {
                    id: 'login_method',
                    label: 'login_method_password',
                    value: 'password',
                    color: '#e65100',
                },
            ],
            [
                {
                    id: 'login_type',
                    label: 'login_type_admin',
                    value: 'admin',
                    color: '#b71c1c',
                },
                {
                    id: 'login_type',
                    label: 'login_type_app',
                    value: 'app',
                    color: '#283593',
                },
            ],
            [
                {
                    id: 'status',
                    label: 'login_success',
                    value: 'success',
                    color: '#2e7d32',
                },
                {
                    id: 'status',
                    label: 'login_failed',
                    value: 'failed',
                    color: '#c62828',
                },
            ],
        ],
        []
    );

    const filteredLogins = useMemo(() => {
        let rows = allLogins;

        if (filters.login_method) {
            rows = rows.filter((row) => row.login_method === filters.login_method);
        }
        if (filters.login_type) {
            rows = rows.filter((row) => row.login_type === filters.login_type);
        }
        if (filters.status) {
            rows = rows.filter((row) => {
                const isPending =
                    row.status === 'pending' || row.failure_reason === 'pending';
                const ok =
                    !isPending &&
                    (row.status === 'success' ||
                        (row.status == null && row.success !== false && row.success !== 0));
                if (filters.status === 'success') return ok;
                if (filters.status === 'failed') return !ok && !isPending;
                if (filters.status === 'pending') return isPending;
                return true;
            });
        }

        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) => {
            const haystack = [
                row.name,
                row.user_id,
                row.kennitala,
                row.phone,
                row.email,
                row.ip_address,
                row.login_method,
                row.login_type,
                row.failure_reason,
                row.failure_message,
                row.device_type,
                row.browser,
                row.os,
                row.city,
                row.country,
                row.country_code,
                row.user_agent,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [allLogins, filters, searchQuery]);

    const getFailureLabel = (failureReason) => {
        if (!failureReason) return null;
        if (/^[a-z0-9_]+$/.test(failureReason)) {
            return t(`login_failure_${failureReason}`, { defaultValue: failureReason });
        }
        return failureReason;
    };

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.logins')} />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        <SparaFilter
                            filters={filters}
                            filterGroups={filterGroups}
                            onFilterClick={handleFilterClick}
                            onClearAll={handleClearAll}
                            search={searchQuery}
                            onSearchChange={handleSearch}
                        />

                        <div className={styles.listHeader}>
                            {!showingSkeleton && (
                                <span className={styles.listHeaderCount}>
                                    {`${t('filter.showing')} ${filteredLogins.length} ${t(
                                        'filter.of'
                                    )} ${totalCount} ${t('menu.logins').toLowerCase()}`}
                                </span>
                            )}
                        </div>

                        <div className={styles.tableWrap}>
                            <Table size="small" sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow className={styles.tableHead}>
                                        <TableCell>{t('user')}</TableCell>
                                        <TableCell>{t('login_method')}</TableCell>
                                        <TableCell>{t('login_type')}</TableCell>
                                        <TableCell>{t('login_status')}</TableCell>
                                        <TableCell>{t('login_error_description')}</TableCell>
                                        <TableCell>{t('login_is_signup')}</TableCell>
                                        <TableCell>{t('login_device')}</TableCell>
                                        <TableCell>{t('login_browser')}</TableCell>
                                        <TableCell>{t('login_location')}</TableCell>
                                        <TableCell>{t('kennitala')}</TableCell>
                                        <TableCell>{t('phone')}</TableCell>
                                        <TableCell>{t('email')}</TableCell>
                                        <TableCell>IP</TableCell>
                                        <TableCell>{t('created')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {showingSkeleton
                                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                              <LoginRowSkeleton key={i} />
                                          ))
                                        : filteredLogins.map((row) => {
                                              const { bg, text } = generateColor(
                                                  row.user_id || row.id
                                              );
                                              const isPending =
                                                  row.status === 'pending' ||
                                                  row.failure_reason === 'pending';
                                              const ok =
                                                  !isPending &&
                                                  (row.status === 'success' ||
                                                      (row.status == null &&
                                                          row.success !== false &&
                                                          row.success !== 0));
                                              const methodKey = String(
                                                  row.login_method || ''
                                              ).toLowerCase();
                                              const methodStyle =
                                                  methodBadgeStyles[methodKey] ||
                                                  methodBadgeStyles[row.login_method] ||
                                                  defaultBadgeStyle;
                                              const typeKey = String(
                                                  row.login_type || ''
                                              ).toLowerCase();
                                              const typeStyle =
                                                  typeBadgeStyles[typeKey] ||
                                                  typeBadgeStyles[row.login_type] ||
                                                  defaultBadgeStyle;
                                              const failureLabel =
                                                  !ok && !isPending
                                                      ? getFailureLabel(row.failure_reason)
                                                      : null;
                                              const failureMessage = isPending
                                                  ? row.failure_message ||
                                                    t('login_pending_description')
                                                  : !ok
                                                    ? row.failure_message || failureLabel || '—'
                                                    : '—';
                                              const statusStyle = isPending
                                                  ? {
                                                        background: 'var(--spara-badge-pending-bg)',
                                                        color: 'var(--spara-badge-pending-fg)',
                                                    }
                                                  : ok
                                                    ? {
                                                          background: 'var(--spara-badge-green-bg)',
                                                          color: 'var(--spara-badge-green-fg)',
                                                      }
                                                    : {
                                                          background: 'var(--spara-badge-fail-bg)',
                                                          color: 'var(--spara-badge-fail-fg)',
                                                      };
                                              const statusLabel = isPending
                                                  ? `… ${t('login_pending')}`
                                                  : ok
                                                    ? `✓ ${t('login_success')}`
                                                    : `✗ ${t('login_failed')}`;
                                              const isSignup =
                                                  row.is_signup === true || row.is_signup === 1;
                                              const deviceType = row.device_type || null;
                                              const deviceStyle =
                                                  deviceBadgeStyles[deviceType] || defaultBadgeStyle;
                                              const deviceLabel = deviceType
                                                  ? t(`login_device_${deviceType}`, {
                                                        defaultValue:
                                                            deviceType.charAt(0).toUpperCase() +
                                                            deviceType.slice(1),
                                                    })
                                                  : null;
                                              const locationParts = [
                                                  row.city,
                                                  row.country_code || row.country,
                                              ].filter(Boolean);
                                              const locationLabel =
                                                  locationParts.length > 0
                                                      ? locationParts.join(', ')
                                                      : null;
                                              const displayName =
                                                  row.name ||
                                                  row.email ||
                                                  row.kennitala ||
                                                  row.phone ||
                                                  t('no_name');
                                              const secondary = row.user_id
                                                  ? `ID ${row.user_id}`
                                                  : row.email && row.name
                                                    ? row.email
                                                    : row.kennitala && row.name
                                                      ? row.kennitala
                                                      : row.phone && row.name
                                                        ? row.phone
                                                        : '—';

                                              return (
                                                  <TableRow
                                                      key={row.id}
                                                      className={styles.tableRow}
                                                      hover
                                                  >
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: 12,
                                                              }}
                                                          >
                                                              <Avatar
                                                                  sx={{
                                                                      width: 38,
                                                                      height: 38,
                                                                      bgcolor: bg,
                                                                      color: text,
                                                                      fontSize: '0.9rem',
                                                                      fontWeight: 600,
                                                                  }}
                                                              >
                                                                  {(
                                                                      displayName?.[0] || '?'
                                                                  ).toUpperCase()}
                                                              </Avatar>
                                                              <div>
                                                                  <p className={styles.userName}>
                                                                      {displayName}
                                                                  </p>
                                                                  <p className={styles.userEmail}>
                                                                      {secondary}
                                                                  </p>
                                                              </div>
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          {row.login_method ? (
                                                              <span
                                                                  className={styles.infoBadge}
                                                                  style={methodStyle}
                                                              >
                                                                  {row.login_method === 'password'
                                                                      ? 'Password'
                                                                      : row.login_method.toUpperCase()}
                                                              </span>
                                                          ) : (
                                                              <span className={styles.metaText}>
                                                                  —
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          {row.login_type ? (
                                                              <span
                                                                  className={styles.infoBadge}
                                                                  style={typeStyle}
                                                              >
                                                                  {row.login_type
                                                                      .charAt(0)
                                                                      .toUpperCase() +
                                                                      row.login_type.slice(1)}
                                                              </span>
                                                          ) : (
                                                              <span className={styles.metaText}>
                                                                  —
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <span
                                                              className={styles.infoBadge}
                                                              style={statusStyle}
                                                          >
                                                              {statusLabel}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          {!ok || isPending ? (
                                                              <span className={styles.metaText}>
                                                                  {failureMessage}
                                                              </span>
                                                          ) : (
                                                              <span className={styles.metaText}>
                                                                  —
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {isSignup ? t('yes') : t('no')}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          {deviceLabel ? (
                                                              <span
                                                                  className={styles.infoBadge}
                                                                  style={deviceStyle}
                                                                  title={row.user_agent || ''}
                                                              >
                                                                  {deviceLabel}
                                                              </span>
                                                          ) : (
                                                              <span
                                                                  className={styles.metaText}
                                                                  title={row.user_agent || ''}
                                                              >
                                                                  —
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <span
                                                              className={styles.metaText}
                                                              title={row.user_agent || ''}
                                                          >
                                                              {row.browser || '—'}
                                                          </span>
                                                          {row.os && (
                                                              <span className={styles.metaSubText}>
                                                                  {row.os}
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {locationLabel || '—'}
                                                          </span>
                                                          {row.country && row.country_code && (
                                                              <span className={styles.metaSubText}>
                                                                  {row.country}
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {row.kennitala || '—'}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {row.phone || '—'}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {row.email || '—'}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {row.ip_address || '—'}
                                                          </span>
                                                      </TableCell>
                                                      <TableCell>
                                                          <span className={styles.metaText}>
                                                              {formatDate(row.created_at) || '—'}
                                                          </span>
                                                          {row.created_at && (
                                                              <span className={styles.metaSubText}>
                                                                  {timeAgo(row.created_at)}
                                                              </span>
                                                          )}
                                                      </TableCell>
                                                  </TableRow>
                                              );
                                          })}
                                    {isLoadingMore &&
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <LoginRowSkeleton key={`more-${i}`} />
                                        ))}
                                </TableBody>
                            </Table>
                        </div>

                        {!showingSkeleton && hasMore && (
                            <button
                                className={styles.loadMoreBtn}
                                onClick={handleLoadMore}
                                disabled={isLoadingMore}
                            >
                                {isLoadingMore ? t('common.loading_more') : t('load_more')}
                            </button>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default ActivityLogins;
